import type { FC } from 'react';
import { useUserStore } from '@Auth/store';
import { isCreateMode, useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { crossCheckBlockUserCtx } from '@ContractBuilder/rules/block/cross-check-block-user-ctx';
import { useEntityStore } from '@ContractBuilder/store';
import { getDocumentContext } from '@ContractBuilder/utils/get-document-context';
import { useModalDispatch } from '@hooks/useModalProvider';
import { isClauseBlock } from '@root/helpers/blocks';
import { isSuperadmin } from '@root/helpers/permissions';
import { MODALS } from '@src/constants';
import { isEqual } from 'lodash-es';
import { useIsPresent } from 'motion/react';

import { useBlockDetails } from '../hooks/useBlockDetails';
import { getPanelMode } from '../utils/get-panel-mode';
import { createToggleField } from '../utils/toggle-form-field';
import { BlockConfigurationPanel } from '../views/BlockConfigurationPanel';

interface BlockConfigurationPanelControllerProps {
  onCancel?: () => void;
}

export const BlockConfigurationPanelController: FC<BlockConfigurationPanelControllerProps> = ({ onCancel }) => {
  const mode = getPanelMode();
  const isPresent = useIsPresent();

  const { currentBlock, formValues, setFormValues } = useBlockEditFormStore(
    ({ currentBlock, formValues, setFormValues }) => ({ currentBlock, formValues, setFormValues }),
    (prev, next) => (isPresent === false ? true : isEqual(prev, next)),
  );

  const submission = useEntityStore(({ submission }) => submission);
  const user = useUserStore((state) => state.user);
  const { showModal } = useModalDispatch();

  const isSuperAdminUser = isSuperadmin(user);
  const isBlockType = !(formValues && isClauseBlock(formValues));

  const details = useBlockDetails({
    currentBlock,
    formValues,
    isAdminView: mode.isAdminView,
    submissionCreatedAt: submission?.created_at,
  });

  const isCreate = isCreateMode(formValues);
  const isAmended = Boolean(currentBlock?.amended_at);

  const { canBeDeletedWithCurrentPermissions } = crossCheckBlockUserCtx(currentBlock, getDocumentContext(), user);
  const canDeleteBlock = Boolean(
    canBeDeletedWithCurrentPermissions && (mode.isAdminView || (formValues?.canDelete ?? true) || isSuperAdminUser),
  );

  const onDelete = () =>
    showModal(MODALS.CONFIRM_DELETE, {
      id: formValues?.id,
      title: formValues?.name,
      type: formValues && isClauseBlock(formValues) ? 'clause' : 'block',
      callback: onCancel,
    });

  const onNameChange = (name: string) => setFormValues((prev) => ({ ...prev, name }));

  const toggleField = createToggleField(setFormValues);

  return (
    <BlockConfigurationPanel
      canDeleteBlock={canDeleteBlock}
      details={details}
      formValues={formValues}
      isAmended={isAmended}
      isBlockType={isBlockType}
      isCreate={isCreate}
      isSuperAdminUser={isSuperAdminUser}
      mode={mode}
      onDelete={onDelete}
      onNameChange={onNameChange}
      setFormValues={setFormValues}
      toggleField={toggleField}
    />
  );
};
